import { useCallback } from 'react';
import { Cabecalho, Layout } from '../componentes/Layout.tsx';
import { Botao, Campo } from '../componentes/forma.tsx';
import { Bancada, Chave, Lista, Painel } from '../componentes/painel.tsx';
import { ErroApi } from '../lib/api.ts';
import { useCadastro } from '../lib/cadastro.ts';
import { usePode, type Permissoes } from '../lib/sessao.tsx';

interface Item {
  id: number;
  nome: string;
  ativo: boolean;
}
type Form = { nome: string; ativo: boolean };

const VAZIO: Form = { nome: '', ativo: true };

/** Especialidades e convênios têm a mesma forma — um nome e um interruptor.
 *  Continuam sendo duas telas porque são dois conceitos para quem usa. */
export function ListaSimples({
  recurso,
  titulo,
  singular,
  permissao,
  descricao,
}: {
  recurso: string;
  titulo: string;
  singular: string;
  permissao: keyof Permissoes;
  descricao: string;
}) {
  const podeEditar = usePode()(permissao);
  const paraFormulario = useCallback((i: Item) => ({ nome: i.nome, ativo: i.ativo }), []);

  const c = useCadastro<Item, Form>({
    recurso,
    vazio: VAZIO,
    paraFormulario,
    rotulo: (i) => i.nome,
  });

  return (
    <Layout>
      <Cabecalho titulo={titulo} contagem={c.lista.isPending ? '' : `${c.total}`}>
        {c.editando !== null && (
          <Botao onClick={c.limpar} variante="neutro">
            Novo {singular.toLowerCase()}
          </Botao>
        )}
      </Cabecalho>

      <Bancada>
        <Painel
          titulo={c.editando !== null ? `Editar ${singular.toLowerCase()}` : `Novo ${singular.toLowerCase()}`}
          editando={c.editando !== null}
          erro={c.erroGeral}
          salvo={c.salvo}
          salvando={c.salvando}
          rotuloCriar={`Cadastrar ${singular.toLowerCase()}`}
          aoEnviar={c.enviar}
          aoCancelar={c.limpar}
          somenteLeitura={!podeEditar}
          excluindo={c.excluir.isPending}
          aoExcluir={() => {
            if (confirm(`Excluir ${c.form.nome}?`)) c.excluir.mutate(c.editando!);
          }}
        >
          <Campo
            rotulo="Nome"
            obrigatorio
            autoFocus
            value={c.form.nome}
            erro={c.campos.nome}
            onChange={c.mudar('nome')}
            disabled={!podeEditar}
          />
          <Chave
            rotulo="Ativo"
            descricao={`${singular} desativado não aparece no cadastro de exames.`}
            checked={c.form.ativo}
            onChange={c.mudar('ativo')}
            disabled={!podeEditar}
          />
        </Painel>

        <div>
          <Lista
            carregando={c.lista.isPending}
            recarregando={c.lista.isFetching}
            erro={c.lista.error instanceof ErroApi ? c.lista.error.message : undefined}
            vazio={
              c.total === 0
                ? { titulo: `Nenhum ${singular.toLowerCase()} cadastrado`, apoio: descricao }
                : undefined
            }
          >
            <table className="tabela">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {c.itens.map((i) => (
                  <tr
                    key={i.id}
                    tabIndex={0}
                    role="button"
                    data-selecionada={c.editando === i.id || undefined}
                    data-inativa={!i.ativo || undefined}
                    onClick={() => c.selecionar(i)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        c.selecionar(i);
                      }
                    }}
                  >
                    <td className="tabela__nome">{i.nome}</td>
                    <td>{i.ativo ? 'Ativo' : 'Desativado'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Lista>
        </div>
      </Bancada>
    </Layout>
  );
}

export const Especialidades = () => (
  <ListaSimples
    recurso="especialidades"
    titulo="Especialidades"
    singular="Especialidade"
    permissao="pode_procedimentos"
    descricao="Cadastre as especialidades que a clínica atende. Elas organizam procedimentos e médicos."
  />
);

export const Convenios = () => (
  <ListaSimples
    recurso="convenios"
    titulo="Convênios"
    singular="Convênio"
    permissao="pode_convenios"
    descricao="Cadastre os convênios atendidos. Eles aparecem no cadastro do exame."
  />
);
